/**
 * Core control family — token-aligned plain objects for inline `style={}`.
 * Re-exported from `uiStyles` for legacy screens; import from `src/ui` for new code.
 */

import { foundation as f } from './tokens';
import {
  badge,
  buttonDanger,
  buttonGhost,
  buttonPrimary,
  buttonSecondary,
  chip,
  field,
  filterInput,
  select,
  selectCompact,
  tab,
  tabActive,
  tabsRow,
  textarea,
} from './primitives';

const { color, spacePx, radii, shadow, fontSize, fontWeight, lineHeight, fontFamily } = f;

/** Gap between icon and label inside buttons / chips */
export const CONTROL_ICON_GAP = '8px';

export const CONTROL_ICON_SM = 16;
export const CONTROL_ICON_MD = 18;

/** Inline row for leading icon + text */
export const rowWithIconLeading = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: CONTROL_ICON_GAP,
};

export const buttonSuccess = {
  fontFamily: fontFamily.sans,
  fontSize: fontSize.sm,
  fontWeight: fontWeight.semibold,
  lineHeight: lineHeight.tight,
  padding: `${spacePx[2]} ${spacePx[4]}`,
  borderRadius: radii.md,
  border: `1px solid ${color.semantic.finishSave}`,
  backgroundColor: color.semantic.finishSave,
  color: color.brand.onPrimary,
  cursor: 'pointer',
  boxShadow: shadow.xs,
};

/** Secondary tabs / subtabs — quieter rhythm */
export const subTab = {
  ...tab,
  fontSize: fontSize.xs,
  fontWeight: fontWeight.medium,
  padding: `${spacePx[2]} ${spacePx[2]}`,
  borderRadius: radii.sm,
};

export const subTabActive = {
  ...tabActive,
  fontWeight: fontWeight.semibold,
};

/** Flex row container for 2–3 inline action buttons */
export const actionButtonRow = {
  display: 'flex',
  gap: '8px',
  flexWrap: 'wrap',
};

/** Secondary-style inline action button (edit, view, manage) */
export const actionButton = {
  fontFamily: fontFamily.sans,
  fontSize: fontSize.sm,
  fontWeight: fontWeight.semibold,
  lineHeight: lineHeight.tight,
  padding: `${spacePx[2]} ${spacePx[3]}`,
  borderRadius: radii.lg,
  border: `1px solid ${color.neutral.borderStrong}`,
  backgroundColor: color.neutral.surface,
  color: color.neutral.text,
  cursor: 'pointer',
};

/** 50%-flex mobile action button variant (fills half-width row) */
export const actionButtonMobile = {
  ...actionButton,
  flex: '1 1 calc(50% - 4px)',
  minWidth: 0,
  padding: `${spacePx[2]} ${spacePx[3]}`,
  fontSize: '0.82rem',
};

/** Danger color override for destructive mobile action button */
export const dangerButtonMobile = {
  backgroundColor: color.semantic.dangerSoft,
  color: color.semantic.danger,
  borderColor: color.semantic.dangerBorder,
};

/**
 * Horizontal scrollable pill-shaped tab strip container.
 * Distinct from flat `tabsRow` — uses rounded pill container with gradient background.
 */
export const pillTabStrip = {
  display: 'flex',
  gap: spacePx[2],
  padding: spacePx[2],
  backgroundColor: color.neutral.surface,
  border: `1px solid ${color.neutral.border}`,
  borderRadius: radii.xl,
  boxShadow: '0 6px 18px rgba(15, 23, 42, 0.06)',
  background: `linear-gradient(180deg, ${color.neutral.surface} 0%, ${color.neutral.bg} 100%)`,
  overflowX: 'auto',
  flexWrap: 'nowrap',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
};

export {
  badge,
  buttonDanger,
  buttonGhost,
  buttonPrimary,
  buttonSecondary,
  chip,
  field,
  filterInput,
  select,
  selectCompact,
  tab,
  tabActive,
  tabsRow,
  textarea,
};
